import { FC, useContext, useEffect } from "react";
import { LearningModuleSummary } from "../_models/types";
import SCORMAdapter from "../_helpers/scormApiAdapter";
import useAPI from "../_hooks/useAPI";
import UserAuthStateContext from "../_contexts/userAuth.context";

declare global {
    interface Window {
        API: any;
    }
}

interface IProps {
    moduleSummary: LearningModuleSummary;
}

const HEARTBEAT_MS = 10000;

const ScormModuleView: FC<IProps> = ({ moduleSummary }) => {
    const { UserAuthState } = useContext(UserAuthStateContext);
    const fetchWrapper = useAPI();
    const apiBase = process.env.REACT_APP_API_URL;
    const lsKey = `scorm:${UserAuthState?.user_id || "anon"}:${moduleSummary.name}`;

    useEffect(() => {
        window.API = new SCORMAdapter(moduleSummary.name, UserAuthState, fetchWrapper);

        (async () => {
            try {
                const initData = await fetchWrapper.get(
                    `${apiBase}/v1/scorm/init_data/?module_name=${moduleSummary.name}`
                );
                localStorage.setItem(lsKey, JSON.stringify(initData || {}));
                localStorage.setItem("scormData", JSON.stringify(initData || {}));
            } catch (err) {
                console.error("Failed to load SCORM init data:", err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleSummary.name]);

    const getSnapshot = () => {
        try {
            if (window.API?.getSnapshot) return window.API.getSnapshot();
            return localStorage.getItem(lsKey) || localStorage.getItem("scormData") || "{}";
        } catch {
            return "{}";
        }
    };


    useEffect(() => {
        let isDestroyed = false;
        let isCommitting = false; // prevent overlapping commits

        const tick = async () => {
            if (isDestroyed || isCommitting) return;
            isCommitting = true;

            try {
                if (typeof window.API?.LMSCommit === "function") {
                    const ok = await window.API.LMSCommit("");
                    if (ok) {
                        // save backup copy
                        const snapshot = getSnapshot();
                        localStorage.setItem(lsKey, snapshot);
                    } else {
                        console.warn("SCORM commit returned false at", new Date().toISOString());
                    }
                } else {
                    const snapshot = getSnapshot();
                    let dataObject = {};
                    try {
                        dataObject = JSON.parse(snapshot);
                    } catch (e) {
                        console.error("Failed to parse SCORM snapshot", e);
                    }

                    await fetchWrapper.post(`${apiBase}/v1/scorm/commit/`, {
                        module_name: moduleSummary.name,
                        data: dataObject,
                    });
                    localStorage.setItem(lsKey, snapshot); // FIX: Use 'snapshot' here
                }
            } catch (e) {
                console.error("SCORM heartbeat commit failed:", e);
            } finally {
                isCommitting = false;
            }
        };

        const id = setInterval(tick, HEARTBEAT_MS);
        return () => {
            isDestroyed = true;
            clearInterval(id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleSummary.name]);

    //  3) Final commit on tab hide/unload
    useEffect(() => {
        const hardCommit = async () => {
            try {
                if (typeof window.API?.LMSCommit === "function") {
                    await window.API.LMSCommit("");
                } else {
                    const snapshot = getSnapshot();
                    let dataObject = {};
                    try {
                        dataObject = JSON.parse(snapshot);
                    } catch (e) {
                        console.error("Failed to parse SCORM snapshot", e);
                    }

                    await fetchWrapper.post(`${apiBase}/v1/scorm/commit/`, {
                        module_name: moduleSummary.name,
                        data: dataObject, // Sending a parsed object
                    });
                }
            } catch (e) {
                console.error("Final SCORM commit failed:", e);
            }
        };

        const onHide = () => hardCommit();
        const onUnload = () => hardCommit();

        document.addEventListener("visibilitychange", onHide);
        window.addEventListener("beforeunload", onUnload);

        return () => {
            document.removeEventListener("visibilitychange", onHide);
            window.removeEventListener("beforeunload", onUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleSummary.name]);

    return (
        <iframe
            id="scorm"
            src={`/${moduleSummary.name}/scormdriver/indexAPI.html`}
            height="700"
            frameBorder="0"
            title="Scorm Render"
            style={{ width: "100%" }}
        />
    );
};

export default ScormModuleView;
